import React, { useState, useRef } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";

const OTP_LENGTH = 4;

const OtpInput = ({ onComplete, onSubmit, disabled = false }) => {
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const inputRefs = useRef([]);

  const focusInput = (index) => {
    inputRefs.current[index]?.focus();
    inputRefs.current[index]?.select();
  };

  const updateOtp = (nextOtp) => {
    setOtp(nextOtp);
    // Clear the parent's code whenever a digit is removed.
    onComplete?.(
      nextOtp.every((digit) => /^[0-9]$/.test(digit)) ? nextOtp.join("") : ""
    );
  };

  const insertDigits = (value, index) => {
    const digits = value.replace(/[^0-9]/g, "");
    if (!digits) return;

    // A complete pasted/autofilled code always starts in the first box.
    const start = digits.length >= OTP_LENGTH ? 0 : index;
    const inserted = digits.slice(0, OTP_LENGTH - start);
    const nextOtp = [...otp];
    [...inserted].forEach((digit, offset) => {
      nextOtp[start + offset] = digit;
    });
    updateOtp(nextOtp);
    focusInput(Math.min(start + inserted.length, OTP_LENGTH - 1));
  };

  const handleChange = (event, index) => {
    const value = event.target.value;
    if (value === "") {
      const nextOtp = [...otp];
      nextOtp[index] = "";
      updateOtp(nextOtp);
      return;
    }
    if (/^[0-9]+$/.test(value)) insertDigits(value, index);
  };

  const handleKeyDown = (event, index) => {
    if (disabled || event.nativeEvent.isComposing) return;

    if (event.key === "Enter") {
      event.preventDefault();
      if (event.repeat) return;
      if (index < OTP_LENGTH - 1) {
        focusInput(index + 1);
      } else {
        const firstEmpty = otp.findIndex((digit) => digit === "");
        if (firstEmpty !== -1) focusInput(firstEmpty);
        else onSubmit?.(otp.join(""));
      }
    } else if (event.key === "Backspace") {
      event.preventDefault();
      const target = otp[index] ? index : Math.max(0, index - 1);
      const nextOtp = [...otp];
      nextOtp[target] = "";
      updateOtp(nextOtp);
      focusInput(target);
    } else if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      focusInput(index - 1);
    } else if (event.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      event.preventDefault();
      focusInput(index + 1);
    }
  };

  return (
    <Box sx={{ display: "flex", gap: { xs: 1, sm: 2 }, mt: 1 }}>
      {otp.map((digit, index) => (
        <TextField
          key={index}
          type="text"
          value={digit}
          disabled={disabled}
          inputRef={(element) => {
            inputRefs.current[index] = element;
          }}
          onChange={(event) => handleChange(event, index)}
          onFocus={(event) => event.target.select()}
          onKeyDown={(event) => handleKeyDown(event, index)}
          onPaste={(event) => {
            event.preventDefault();
            if (!disabled)
              insertDigits(event.clipboardData.getData("text"), index);
          }}
          inputProps={{
            inputMode: "numeric",
            pattern: "[0-9]*",
            autoComplete: index === 0 ? "one-time-code" : "off",
            enterKeyHint: index < OTP_LENGTH - 1 ? "next" : "done",
            // Allow a full code to reach onChange for browser autofill.
            maxLength: OTP_LENGTH,
            "aria-label": `OTP digit ${index + 1} of ${OTP_LENGTH}`,
            style: { textAlign: "center", padding: "14px 0", fontSize: "20px" },
          }}
          sx={{ width: { xs: 52, sm: 64 }, minWidth: 0 }}
          variant="outlined"
        />
      ))}
    </Box>
  );
};

export default OtpInput;
